import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, ExternalLink, Heart, MessageCircle } from "lucide-react";
import { useSocialFeed } from "../hooks/useSocialFeed";
import type { FacebookFeedResponse, InstagramFeedResponse } from "../types/social";

function timeAgo(dateString: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000
  );
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function PostSkeleton() {
  return (
    <div className="card overflow-hidden">
      <div className="aspect-square bg-gray-200 rounded-lg mb-4 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-full" />
      <div className="h-4 bg-gray-200 rounded animate-pulse mb-4 w-2/3" />
      <div className="flex items-center space-x-4">
        <div className="h-3 bg-gray-200 rounded animate-pulse w-12" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-12" />
      </div>
    </div>
  );
}

function FeedFallback({
  platform,
  href,
  icon: Icon,
}: {
  platform: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="col-span-full text-center py-16">
      <Icon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
      <p className="text-gray-500 mb-6">
        We couldn't load our latest {platform} posts right now.
      </p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center space-x-2 bg-sage-600 hover:bg-sage-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
      >
        <span>Visit us on {platform}</span>
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full text-center py-12">
      <p className="text-gray-500">No posts to show right now.</p>
    </div>
  );
}

const Socials = () => {
  const facebook = useSocialFeed<FacebookFeedResponse>("facebook-feed");
  const instagram = useSocialFeed<InstagramFeedResponse>("instagram-feed");

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Follow Our Journey
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Stay connected with our latest projects, design inspiration, and
              behind-the-scenes moments across our social channels.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Facebook Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                  <Facebook className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Facebook</h2>
                  <p className="text-gray-600">Sandhurst Interiors</p>
                </div>
              </div>
              <a
                href="https://www.facebook.com/sandhurstinteriors/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sage-600 hover:text-sage-700 transition-colors duration-200"
              >
                <span>Follow</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {facebook.loading ? (
                <>
                  <PostSkeleton />
                  <PostSkeleton />
                  <PostSkeleton />
                </>
              ) : facebook.error ? (
                <FeedFallback
                  platform="Facebook"
                  href="https://www.facebook.com/sandhurstinteriors/"
                  icon={Facebook}
                />
              ) : !facebook.data?.posts.length ? (
                <EmptyState />
              ) : (
                facebook.data.posts.map((post, i) => (
                  <motion.a
                    key={post.id}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: i % 2 === 0 ? -100 : 100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
                    className="card overflow-hidden hover:shadow-xl transition-shadow duration-300 block"
                  >
                    {post.imageUrl && (
                      <div className="aspect-square bg-gray-200 rounded-lg mb-4 overflow-hidden">
                        <img
                          src={post.imageUrl}
                          alt={post.message || "Facebook post"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {post.message && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {post.message}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Heart className="w-4 h-4" />
                          <span>{post.likeCount}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <MessageCircle className="w-4 h-4" />
                          <span>{post.commentCount}</span>
                        </span>
                      </div>
                      <span className="text-xs">{timeAgo(post.createdTime)}</span>
                    </div>
                  </motion.a>
                ))
              )}
            </div>
          </motion.div>

          {/* Instagram Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Instagram className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Instagram</h2>
                  <p className="text-gray-600">@sandhurstinteriors</p>
                </div>
              </div>
              <a
                href="https://www.instagram.com/sandhurstinteriors/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sage-600 hover:text-sage-700 transition-colors duration-200"
              >
                <span>Follow</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {instagram.loading ? (
                <>
                  <PostSkeleton />
                  <PostSkeleton />
                  <PostSkeleton />
                </>
              ) : instagram.error ? (
                <FeedFallback
                  platform="Instagram"
                  href="https://www.instagram.com/sandhurstinteriors/"
                  icon={Instagram}
                />
              ) : !instagram.data?.posts.length ? (
                <EmptyState />
              ) : (
                instagram.data.posts.map((post, i) => (
                  <motion.a
                    key={post.id}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: i % 2 === 0 ? -100 : 100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
                    className="card overflow-hidden hover:shadow-xl transition-shadow duration-300 block"
                  >
                    <div className="aspect-square bg-gray-200 rounded-lg mb-4 overflow-hidden">
                      <img
                        src={post.mediaUrl}
                        alt={post.caption || "Instagram post"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {post.caption && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {post.caption}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Heart className="w-4 h-4" />
                          <span>{post.likeCount}</span>
                        </span>
                      </div>
                      <span className="text-xs">{timeAgo(post.timestamp)}</span>
                    </div>
                  </motion.a>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Get in touch to discuss your project and bring your vision to
              life.
            </p>
            <div className="flex justify-center">
              <Link
                to="/contact#contact-form"
                onClick={() => {
                  sessionStorage.setItem("scrollToContactForm", "true");
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Socials;
